import { i18n } from '@/i18n';
import { DocumentType } from '@/model/inventory';
import { useAccountStore } from '@/stores/AccountStore/AccountStore';
import { useInventoryStore } from '@/stores/InventoryStore/InventoryStore';
import { type NeonSearchOption } from '@art-basel/artbasel-lib-neon';
import {
  type ArcualAccountProfile,
  type ArcualInventoryItem,
  ArcualItemCategory,
  ArcualItemType,
  type ArcualPrice,
  ArcualPriceVisibility,
} from '@art-basel/artbasel-lib-platform';

const { t } = i18n.global;

const getAccountById = async (id?: string, account?: ArcualAccountProfile) =>
  account || (id ? useAccountStore().getAccountById(id) : undefined);

/**
 * enrich inventory item with missing reference data
 * @param item
 */
export const enrichInventoryItem = async (item: ArcualInventoryItem) => {
  if (!item.seller && item.sellerProfileId) {
    item.seller = await getAccountById(item.sellerProfileId, item.seller);
  }
  if (!item.owner && item.ownerProfileId) {
    item.owner = await getAccountById(item.ownerProfileId, item.owner);
  }
  if (!item.creator && item.originatorProfileId) {
    item.creator = await getAccountById(item.originatorProfileId, item.creator);
  }

  if (!item.actions) {
    item.actions = await useInventoryStore().getItemActions(item.id);
  }

  if (
    (item.itemType === ArcualItemType.EDITION || item.itemType === ArcualItemType.ARTIST_PROOF) &&
    item.childMetadata &&
    !item.childMetadata?.volume &&
    item.masterMetadata
  ) {
    item.childMetadata.volume =
      item.itemType === ArcualItemType.EDITION
        ? item.masterMetadata.editionsVolume
        : item.masterMetadata.artistProofsVolume;
  }
};

// TODO: these should be translated
export const categories = [
  { label: t('platform.app.gallery.artworks.typePlaceholder'), key: '' },
  { label: ArcualItemCategory.PAINTING, key: 'Painting' },
  { label: ArcualItemCategory.PRINT, key: 'Prints and editions' },
  { label: ArcualItemCategory.WORK_ON_PAPER, key: 'Work on paper' },
  { label: ArcualItemCategory.SCULPTURE, key: 'Sculpture' },
  { label: ArcualItemCategory.PHOTOGRAPHY, key: 'Photography' },
  { label: ArcualItemCategory.INSTALLATION, key: 'Installation' },
  { label: ArcualItemCategory.VIDEO, key: 'Video' },
  { label: ArcualItemCategory.DIGITAL_ART, key: 'Digital art' },
  { label: ArcualItemCategory.OTHER, key: 'Other' },
];

export const units: Array<NeonSearchOption> = [
  { label: 'cm', key: 'cm' },
  { label: 'inch', key: 'inch' },
  { label: 'px', key: 'px' },
  { label: 'hh:mm:ss', key: 'duration' },
  { label: 'volume m³', key: 'volumeCubicMeters' },
  { label: 'volume ft³', key: 'volumeCubicFeet' },
  { label: 'other', key: 'other' },
];

export const documentTypes: Array<NeonSearchOption> = Object.values(DocumentType).map((type) => ({
  label: type,
  key: type,
}));

export const areTwoArcualPricesSame = (
  priceOne: ArcualPrice | undefined,
  priceTwo: ArcualPrice | undefined,
): boolean => {
  return (
    (typeof priceOne === 'undefined' && typeof priceTwo === 'undefined') ||
    ((priceOne as ArcualPrice)?.amount === (priceTwo as ArcualPrice)?.amount &&
      (priceOne as ArcualPrice)?.currency === (priceTwo as ArcualPrice)?.currency)
  );
};

export const areInventoryPricesSame = (
  itemOne: ArcualInventoryItem | undefined,
  itemTwo: ArcualInventoryItem | undefined,
): boolean => {
  const isRetailPriceSame = areTwoArcualPricesSame(itemOne?.retailPrice, itemTwo?.retailPrice);
  const isListPriceSame = areTwoArcualPricesSame(itemOne?.listPrice, itemTwo?.listPrice);
  const isToPriceSame = areTwoArcualPricesSame(itemOne?.toPrice, itemTwo?.toPrice);
  const isListingCurrencySame = itemOne?.listingCurrency === itemTwo?.listingCurrency;
  const isPriceVisibilitySame = itemOne?.priceVisibility === itemTwo?.priceVisibility;

  return isRetailPriceSame && isListPriceSame && isToPriceSame && isListingCurrencySame && isPriceVisibilitySame;
};

export const areCollaboratorIdsSame = (
  collaboratorsOne: string[] | undefined,
  collaboratorsTwo: string[] | undefined,
): boolean => {
  if (!collaboratorsOne && !collaboratorsTwo) {
    return true;
  }

  if (Array.isArray(collaboratorsOne) && Array.isArray(collaboratorsTwo)) {
    if (collaboratorsOne.length !== collaboratorsTwo.length) {
      return false;
    }

    const glue = ',';
    return [...collaboratorsOne].sort().join(glue) === [...collaboratorsTwo].sort().join(glue);
  }

  return collaboratorsOne === collaboratorsTwo;
};

export const getNewPriceTextForSuccessAlert = (item: ArcualInventoryItem, texts: Record<string, string>): string => {
  if (!item) {
    return '';
  }

  if (item.priceVisibility === ArcualPriceVisibility.ON_REQUEST) {
    return texts[ArcualPriceVisibility.ON_REQUEST as string] || (item.priceVisibility as string);
  }

  if (item.priceVisibility === ArcualPriceVisibility.RANGE) {
    return [item.listPrice?.currency || item.listingCurrency || '', [item.listPrice?.amount, item.toPrice?.amount].filter((x) => !!x).join('–')]
      .filter((x) => !!x)
      .join(' ');
  }

  return [item.listPrice?.currency || item.listingCurrency, item.listPrice?.amount].filter((x) => !!x).join(' ');
};
